import { useToast } from '@act/react-components/app/ReusableToast';
import { ConsentDisplay } from '@act/react-components/consents/consent-display';
import { ConsentFormType } from '@act/react-components/consents/guide-view/ConsentTermForm';
import {
  ConsentFormSchema,
  ConsentGoalDto,
  ConsentIdentitySchema,
  IdentitySubmissionDto,
} from '@act/shared/data-transfer-objects';
import { Button, FormBuilder } from '@act/storybook';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { selectedMemberSelectors } from 'redux/redux';
import { consentRequests } from 'redux/requests/consents.requests';
import {
  consentActions,
  consentAppSelectors,
  consentIdentitySuccess,
  consentTermSuccess,
} from 'redux/slices/consent.slice';
import { useAppDispatch } from 'redux/store-hooks';
import { useApiCall } from 'redux/utils/api-hook';
import { FormConfig } from 'stories/forms/FormBuilder/FormBuilder.types';
import CloseIcon from '@mui/icons-material/Close';
import { GoalsSidePanel } from '@act/react-components/needs/need-detail-dialog/GoalsSidePanel';
import z from 'zod';
import { ArrowLeftIcon } from '@phosphor-icons/react/dist/ssr/ArrowLeft';
import { useTracking } from '@act/react-components/tracking';
import { setSidePanelIsDirty } from './FullScreenSaveClose.utils';
import useUnsavedChangesDialog from '@act/react-components/dialog-forms/hooks/use-unsaved-changes-dialog';

export type IdentitySubmissionType = z.infer<typeof ConsentIdentitySchema>;

export type ConsentDrawerProps = {
  selectedConsent: ConsentGoalDto;
  onClose: () => void;
  open: boolean;
  onAnimationComplete?: () => void;
};

export const ConsentDrawer = ({
  selectedConsent,
  onClose,
  open,
  onAnimationComplete,
}: ConsentDrawerProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const dispatch = useAppDispatch();
  const { generateTrackingProps } = useTracking();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const userId = selectedMemberSelectors.useSelectSelectedMemberId();
  const consentState = consentAppSelectors.useSelectConsentState();
  const termIdentityId = consentAppSelectors.useSelectTermIdentityId();
  const [termResponse, _isSubmittingTerm, updateTerm] = useApiCall(
    consentRequests.updateConsent
  );
  const [identityResponse, _isSubmittingIdentity, submitIdentity] = useApiCall(
    consentRequests.postIdentity
  );
  const [showIdentityForm, setShowIdentityForm] = useState(() => {
    return (
      !!selectedConsent?.term?.identityRequired && !consentState.termIdentityId
    );
  });

  const identityForm = useForm<IdentitySubmissionType>({
    mode: 'onChange',
    defaultValues: {
      name: undefined,
      dateOfBirth: undefined,
      email: undefined,
      line1: undefined,
      city: undefined,
      state: undefined,
      zip: undefined,
    },
    resolver: zodResolver(ConsentIdentitySchema),
  });

  const consentForm = useForm<ConsentFormType>({
    mode: 'onChange',
    defaultValues: {
      termId: selectedConsent?.term?.id ?? undefined,
      consented: undefined,
      consentIdentityId: termIdentityId ?? undefined,
    },
    resolver: zodResolver(ConsentFormSchema),
  });

  const { control, formState, handleSubmit, reset } = showIdentityForm
    ? identityForm
    : consentForm;

  const consentedValue = consentForm.watch('consented');

  const submitConsent = async (
    data: ConsentFormType | IdentitySubmissionType
  ) => {
    if (showIdentityForm) {
      submitIdentity({
        params: null,
        body: data as IdentitySubmissionDto,
      });
      setShowIdentityForm(false);
    } else {
      updateTerm({
        params: { userId },
        body: {
          termId: selectedConsent?.term?.id ?? undefined,
          consented: consentedValue,
          consentIdentityId: selectedConsent?.term?.identityRequired
            ? termIdentityId
            : null,
        },
      });
    }
  };

  const { isDirty } = formState;

  useEffect(() => {
    if (open) {
      setSidePanelIsDirty(isDirty);
    }
  }, [isDirty, open]);

  useEffect(() => {
    if (!open) {
      setSidePanelIsDirty(false);
    }
    return () => {
      setSidePanelIsDirty(false);
    };
  }, [open]);

  useEffect(() => {
    if (selectedConsent?.term?.id) {
      dispatch(consentActions.reset());
    }
  }, [selectedConsent?.term?.id, dispatch]);

  useEffect(() => {
    const shouldShowIdentityForm =
      !!selectedConsent?.term?.identityRequired && !consentState.termIdentityId;
    setShowIdentityForm(shouldShowIdentityForm);
  }, [selectedConsent?.term?.identityRequired, consentState.termIdentityId]);

  useEffect(() => {
    if (termResponse) {
      dispatch(consentTermSuccess(termResponse));
      onClose();
      toast('Consent updated successfully', 'success');
    }
  }, [termResponse, dispatch, onClose]);

  useEffect(() => {
    if (identityResponse) {
      dispatch(consentIdentitySuccess(identityResponse));
    }
  }, [identityResponse, dispatch]);

  const options = [
    {
      key: 'accept',
      label: selectedConsent?.term?.acceptText ?? '',
      value: true,
    },
  ];
  if (selectedConsent?.term?.rejectText) {
    options.push({
      key: 'reject',
      label: selectedConsent?.term?.rejectText ?? '',
      value: false,
    });
  }

  const formConfig: FormConfig<ConsentFormType> = [
    {
      type: 'input',
      name: 'consented',
      label: t(selectedConsent?.term?.name ?? ''),
      input: 'radioGroup',
      props: {
        options: options,
        required: true,
        row: true,
      },
    },
  ];

  const identityFormConfig: FormConfig<IdentitySubmissionType> = [
    {
      type: 'input',
      name: 'name',
      label: t('consentForm.memberName'),
      input: 'text',
      props: {
        required: true,
      },
    },
    {
      type: 'group',
      items: [
        {
          type: 'input',
          name: 'dateOfBirth',
          label: t('consentForm.dateOfBirth'),
          input: 'date',
          props: {
            required: true,
            inputType: 'date',
          },
        },
        {
          type: 'input',
          name: 'email',
          label: t('consentForm.memberEmail'),
          input: 'text',
          props: {
            required: false,
          },
        },
      ],
    },

    {
      type: 'input',
      name: 'line1',
      label: t('consentForm.memberStreetAddress'),
      input: 'text',
      props: {
        required: false,
      },
    },
    {
      type: 'input',
      name: 'city',
      label: t('consentForm.city'),
      input: 'text',
      props: {
        required: false,
      },
    },
    {
      type: 'group',
      items: [
        {
          type: 'input',
          name: 'state',
          label: t('consentForm.state'),
          input: 'text',
          props: {
            required: false,
          },
        },
        {
          type: 'input',
          name: 'zip',
          label: t('consentForm.zip'),
          input: 'text',
          props: {
            required: false,
          },
        },
      ],
    },
  ];

  const identityRequired = !!selectedConsent?.term?.identityRequired;
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (open) {
      setShouldRender(true);
    }
  }, [open]);

  const handleAnimationComplete = () => {
    setShouldRender(false);
    onAnimationComplete?.();
  };

  if (!shouldRender) return null;
  const handleClose = () => {
    if (isDirty) {
      void handleUnsavedChanges(() => {
        reset();
        onClose();
      });
    } else {
      reset();
      onClose();
    }
  };

  return (
    <GoalsSidePanel
      open={open}
      animated={true}
      setShouldRenderSidePanel={handleAnimationComplete}
      titleContent={
        identityRequired && !showIdentityForm ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: 'row',
            }}
          >
            <ArrowLeftIcon
              size={32}
              onClick={() => setShowIdentityForm(true)}
            />
            <CloseIcon onClick={handleClose} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <CloseIcon onClick={handleClose} />
          </Box>
        )
      }
      content={
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            minHeight: 0,
            padding: '16px',
            gap: '16px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography variant="titleLarge">
            {t(selectedConsent?.term?.name ?? '')}
          </Typography>
          {showIdentityForm ? (
            <FormBuilder
              key="identity-form"
              config={identityFormConfig}
              control={control}
              formState={formState}
            />
          ) : (
            <>
              <ConsentDisplay term={selectedConsent?.term} />
              <Box sx={{ gap: '16px' }}>
                <FormBuilder
                  key="consent-form"
                  config={formConfig}
                  control={control}
                  formState={formState}
                />
              </Box>
            </>
          )}
        </Box>
      }
      footerContent={
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          {showIdentityForm ? (
            <Button
              variant="outlined"
              onClick={handleSubmit(submitConsent)}
              {...generateTrackingProps(
                'consent-identity-from-call-submit-button'
              )}
            >
              Continue to consent
            </Button>
          ) : (
            <Button
              variant="outlined"
              onClick={handleSubmit(submitConsent)}
              {...generateTrackingProps('consent-from-call-submit-button')}
            >
              Save and close
            </Button>
          )}
        </Box>
      }
    />
  );
};
